import { FileText, GitBranch, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils/format';
import { titleCase } from '@/lib/utils/format';
import type { SnapshotListItem } from '@/lib/api/snapshots';

/**
 * Snapshots-list tile for the engagement overview (T-0097).
 *
 * Mirrors the prototype's `snapshotItem(...)` rows on the overview:
 *   git-branch icon · "Pulse Report — 2026-05-10" · meta line
 *
 * The "Open" link for individual snapshots is left as a placeholder
 * because the per-snapshot read route lives outside this ticket's scope
 * (T-0101 owns `snapshot.tsx`). We render the row but skip the trailing
 * "Open" link — once T-0101 lands, swap in a `<Link to="...">`.
 *
 * The "Capture" button at the top wires to the same mutation the hero
 * CTA uses; rendered here so the snapshots card is self-contained on
 * smaller viewports where the user might scroll past the hero CTA.
 */
export interface SnapshotsListProps {
  snapshots: SnapshotListItem[];
  /** Cap on rendered rows (default 5). */
  limit?: number;
  /** Wires the inline Capture button. Disabled when omitted. */
  onCaptureSnapshot?: () => void;
  capturingSnapshot?: boolean;
  /**
   * Wires the per-row "View PDF" button (T-0115 / `data-open-pdf`).
   * When omitted, the affordance is hidden — keeps the snapshot list
   * usable in scopes that haven't wired the modal yet.
   */
  onOpenPdf?: (snapshotId: string) => void;
}

const KIND_LABEL: Record<string, string> = {
  pulse: 'Weekly pulse',
  posture: 'Health Check posture',
  readiness: 'Go-Live readiness',
  engagement: 'Engagement overview',
};

export function SnapshotsList({
  snapshots,
  limit = 5,
  onCaptureSnapshot,
  capturingSnapshot,
  onOpenPdf,
}: SnapshotsListProps) {
  const recent = snapshots.slice(0, limit);
  return (
    <section
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      aria-labelledby="snapshots-list-title"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 id="snapshots-list-title" className="t-h3">
          Snapshots
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCaptureSnapshot}
          disabled={!onCaptureSnapshot || capturingSnapshot}
        >
          <Plus className="size-3" aria-hidden="true" />
          {capturingSnapshot ? 'Capturing…' : 'Capture'}
        </Button>
      </div>

      {recent.length === 0 ? (
        <p className="text-body-sm text-ink-500">
          No snapshots yet. Capture one to freeze a point-in-time view of the engagement.
        </p>
      ) : (
        <ul className="flex flex-col" role="list">
          {recent.map((snap, idx) => (
            <li key={snap.id} className={idx === 0 ? '' : 'mt-2.5 border-t border-ink-100 pt-2.5'}>
              <div className="flex items-start gap-3">
                <GitBranch className="mt-0.5 size-4 shrink-0 text-ink-500" aria-hidden="true" />
                <div className="min-w-0 flex-1 leading-[1.4]">
                  <div className="text-body-sm font-medium text-ink-900">
                    {titleCase(snap.snapshot_type)} report — {formatDate(snap.captured_at)}
                  </div>
                  <div className="t-meta">
                    {KIND_LABEL[snap.snapshot_type] ?? titleCase(snap.snapshot_type)} · seq{' '}
                    <span className="font-mono">{snap.sequence_no}</span>
                  </div>
                </div>
                {onOpenPdf ? (
                  <button
                    type="button"
                    data-open-pdf={snap.id}
                    onClick={() => onOpenPdf(snap.id)}
                    className="inline-flex h-7 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-2 text-meta font-medium text-ink-700 hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                  >
                    <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>PDF</span>
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
